import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, History, Ban, FileSpreadsheet, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { SimpleModal, errorText } from './SimpleModal';
import { downloadFile, todayStamp } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;
const fold = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');
const SORTS = [['recent', 'Cadastro mais recente'], ['logins', 'Mais acessos'], ['alpha', 'Nome (A-Z)']];
const sorters = {
  recent: (a, b) => (b.created_at || '').localeCompare(a.created_at || ''),
  logins: (a, b) => (b.logins || 0) - (a.logins || 0) || fold(a.nome).localeCompare(fold(b.nome)),
  alpha: (a, b) => fold(a.nome).localeCompare(fold(b.nome), 'pt-BR'),
};

const pill = (bg, color) => ({ padding: '0.25rem 0.75rem', borderRadius: 50, fontSize: '0.8rem', fontWeight: 500, background: bg, color });
const TypeTag = ({ sub }) => (sub.simulated ? <span data-testid={`subscriber-simulated-tag-${sub.id}`} style={pill('#fef3c7', '#92400e')}>Simulação</span>
  : sub.isento ? <span style={pill('#e9d8fd', '#553c9a')}>Isento</span> : <span style={pill('#e2e8f0', '#4a5568')}>Regular</span>);
const StatusTag = ({ sub }) => {
  const [bg, color] = sub.simulated ? ['#fef3c7', '#92400e'] : sub.suspended ? ['#e2e8f0', '#4a5568'] : sub.status === 'active' ? ['#c6f6d5', '#22543d'] : sub.status === 'pending' ? ['#fef3c7', '#92400e'] : ['#fed7d7', '#9b2c2c'];
  return <span style={pill(bg, color)} data-testid={`subscriber-status-${sub.id}`}>{sub.status_label}</span>;
};
const SignupDate = ({ sub }) => {
  const started = sub.subscription_start && day(sub.subscription_start) !== day(sub.created_at);
  return (
    <div data-testid={`subscriber-signup-date-${sub.id}`}>
      <div>{day(sub.created_at)}</div>
      {started && <div style={{ fontSize: '0.75rem', color: '#718096' }}>Assinou em {day(sub.subscription_start)}</div>}
    </div>
  );
};

const SuspendModal = ({ sub, headers, onClose, onDone }) => {
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/digital/subscribers/${sub.id}/suspension-request`, { motivo }, { headers });
      toast.success(data.message);
      onDone();
    } catch (e) { toast.error(errorText(e, 'Não foi possível suspender')); } finally { setBusy(false); }
  };
  return (
    <SimpleModal title={`Suspender ${sub.nome}`} onClose={onClose} testId="suspend-modal" maxWidth={520}>
      <p style={{ fontSize: '0.88rem', color: '#4a5568', marginBottom: 10 }}>O leitor perde o acesso à sua biblioteca na hora. O administrador da Livrum recebe o pedido com o motivo e pode confirmar ou reverter a suspensão.</p>
      <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={1000} rows={4} placeholder="Motivo da suspensão (obrigatório)" data-testid="suspend-reason-input"
        style={{ width: '100%', border: '1px solid #cbd5e0', borderRadius: 8, padding: '0.6rem', fontSize: '0.9rem', resize: 'vertical' }} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <Button variant="outline" onClick={onClose} data-testid="suspend-cancel-btn">Cancelar</Button>
        <Button onClick={send} disabled={busy || motivo.trim().length < 5} style={{ background: '#e53e3e' }} data-testid="suspend-confirm-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suspender e avisar o administrador'}
        </Button>
      </div>
    </SimpleModal>
  );
};

const Toolbar = ({ q, setQ, sort, setSort, onExport, exporting }) => (
  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
    <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
      <Search className="w-4 h-4" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome" style={{ paddingLeft: 32 }} data-testid="subscribers-search-input" />
    </div>
    <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="subscribers-sort-select"
      style={{ height: 40, border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 0.75rem', fontSize: '0.9rem', background: 'white' }}>
      {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <Button variant="outline" onClick={onExport} disabled={exporting} data-testid="subscribers-export-btn" style={{ marginLeft: 'auto' }}>
      {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" style={{ color: '#217346' }} />}Exportar planilha
    </Button>
  </div>
);

const Pager = ({ page, pages, total, setPage }) => (pages > 1 ? (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#4a5568' }} data-testid="subscribers-pager">
    <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}</span>
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="subscribers-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
      <span data-testid="subscribers-page-indicator">Página {page} de {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="subscribers-next-page"><ChevronRight className="w-4 h-4" /></Button>
    </div>
  </div>
) : null);

const Row = ({ sub, onHistory, onSuspend }) => (
  <TableRow data-testid={`subscriber-row-${sub.id}`}>
    <TableCell style={{ fontWeight: 500 }}>{sub.nome}</TableCell>
    <TableCell>{sub.email}</TableCell>
    <TableCell>{sub.cpf}</TableCell>
    <TableCell><TypeTag sub={sub} /></TableCell>
    <TableCell><StatusTag sub={sub} /></TableCell>
    <TableCell data-testid={`subscriber-logins-${sub.id}`}>{sub.logins || 0}</TableCell>
    <TableCell><SignupDate sub={sub} /></TableCell>
    <TableCell>
      <Button variant="ghost" size="sm" onClick={() => onHistory(sub)} title="Histórico do usuário" style={{ color: '#3182ce' }} data-testid={`subscriber-history-${sub.id}`}><History className="w-4 h-4" /></Button>
      {!sub.simulated && !sub.trial_reader && (
        <Button variant="ghost" size="sm" onClick={() => onSuspend(sub)} disabled={sub.suspended} title={sub.suspended ? 'Usuário suspenso' : 'Suspender usuário'} style={{ color: '#e53e3e' }} data-testid={`subscriber-suspend-${sub.id}`}><Ban className="w-4 h-4" /></Button>
      )}
    </TableCell>
  </TableRow>
);

export const SubscribersTable = ({ subscribers, headers, onHistory, onChanged }) => {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('recent');
  const [page, setPage] = useState(1);
  const [suspending, setSuspending] = useState(null);
  const [exporting, setExporting] = useState(false);
  const list = useMemo(() => subscribers.filter((s) => fold(s.nome).includes(fold(q.trim()))).sort(sorters[sort]), [subscribers, q, sort]);
  const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const rows = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const exportXlsx = async () => {
    setExporting(true);
    try { await downloadFile(`${API}/digital/subscribers/export`, headers, `assinantes-${todayStamp()}.xlsx`); } catch (e) { toast.error('Não foi possível exportar a planilha'); } finally { setExporting(false); }
  };
  return (
    <>
      <Toolbar q={q} setQ={(v) => { setQ(v); setPage(1); }} sort={sort} setSort={(v) => { setSort(v); setPage(1); }} onExport={exportXlsx} exporting={exporting} />
      <Card>
        <CardContent style={{ padding: 0 }}>
          <Table data-testid="subscribers-table">
            <TableHeader>
              <TableRow>
                {['Nome', 'E-mail', 'CPF', 'Tipo', 'Status', 'Acessos', 'Data de cadastro/assinatura', 'Ações'].map((h) => <TableHead key={h}>{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#718096' }} data-testid="subscribers-empty">{q ? 'Nenhum assinante com esse nome.' : 'Nenhum assinante cadastrado.'}</TableCell></TableRow>
              ) : rows.map((sub) => <Row key={sub.id} sub={sub} onHistory={onHistory} onSuspend={setSuspending} />)}
            </TableBody>
          </Table>
          <Pager page={current} pages={pages} total={list.length} setPage={setPage} />
        </CardContent>
      </Card>
      {suspending && <SuspendModal sub={suspending} headers={headers} onClose={() => setSuspending(null)} onDone={() => { setSuspending(null); onChanged(); }} />}
    </>
  );
};
